import { Card, Table, Popover, Select, Input } from 'antd'
import { Link } from 'react-router-dom'
import { getTableXwidth } from '@configs/table'
import { CommonFilters, CommonSelector, ReplayRecord, TableDownload } from '@components'
import { trimStr, ExpandCallRecordDataType, formatTime, handleT3CCallRecord } from '@library'
import { useTableService } from '@hooks/useTableService'
import { exportT3CRobotCallRecordList, listT3CRobotCallRecord } from '@services/robot'
import { T3CRobotCallRecordItem, T3CCallEndReasonEnum } from '@types'
import { TableColumnProps } from '@hooks'
import { FieldItem } from 'src/components/CommonFilters/configs'
import { ListCallRollTitle, ListCallTag, t3cAgentList } from 'src/components/CommonSelector/configs'
import { useMemo, useRef } from 'react'

type RobotCallRecordData = T3CRobotCallRecordItem & ExpandCallRecordDataType

const initFilter = {
  page: 1,
  pageSize: 10,
}

export default function TCCCRobotCallRecord () {
  const filterRef = useRef<any>()

  const {
    loading,
    list,
    pagination,
    filter,
    setFilter,
    resetFilter,
    rowKey,
  } = useTableService(listT3CRobotCallRecord, initFilter, '获取TCCC机器人外呼通话列表失败')

  const robotCallRecordFilterItems: FieldItem[] = [
    { fieldName: 'rollId', label: '名单标题', render: () => <CommonSelector {...ListCallRollTitle} /> },
    { fieldName: 'agentId', label: '智能体名称', render: () => <CommonSelector {...t3cAgentList} disableItem={null} /> },
    {
      fieldName: 'tags',
      label: '标签',
      render: () => <CommonSelector {...ListCallTag} mode="multiple" />,
    },
    {
      fieldName: 'phone',
      label: '被叫电话',
      type: 'input',
    },
    {
        fieldName: 'failReason',
        label: '通话结果',
        render: () => {
          const options = [
            { label: '接听成功', value: T3CCallEndReasonEnum.OK },
            { label: '客户端错误', value: 'error' },
            { label: '线路异常', value: T3CCallEndReasonEnum.OPERATORERROR },
            { label: '客户挂断', value: 'hangUp' },
            { label: '坐席不在线', value: T3CCallEndReasonEnum.NOSEATONLINE },
            { label: '非工作时间', value: T3CCallEndReasonEnum.NOTWORKTIME },
            { label: 'IVR异常结束', value: T3CCallEndReasonEnum.IVREND },
            { label: '黑名单', value: T3CCallEndReasonEnum.BLACKLIST },
            { label: '未接通', value: T3CCallEndReasonEnum.UNCONNECTED },
            { label: '被叫高风险', value: T3CCallEndReasonEnum.RESTRICTEDCALLEE },
            { label: '无人接听', value: T3CCallEndReasonEnum.NOTANSWER },
            { label: '拒接', value: T3CCallEndReasonEnum.USERREJECT },
            { label: '被叫关机', value: T3CCallEndReasonEnum.POWEROFF },
            { label: '被叫空号', value: T3CCallEndReasonEnum.NUMBERNOTEXIST },
            { label: '正在通话中', value: T3CCallEndReasonEnum.BUSY },
            { label: '停机', value: T3CCallEndReasonEnum.OUTOFCREDIT },
            { label: '主叫挂断', value: T3CCallEndReasonEnum.CALLERCANCEL },
            { label: '不在服务区', value: T3CCallEndReasonEnum.NOTINSERVICE },
            { label: '未知错误', value: T3CCallEndReasonEnum.UNKNOWNERROR },
            { label: '语音信箱挂断', value: T3CCallEndReasonEnum.VOICEMAIL },
          ]
          return (
            <Select
              showArrow
              mode="multiple"
              placeholder="未筛选则默认全部"
              allowClear
              options={options}
            />)
        },
        handleValue: (val) => {
          if (val && val.length) {
            const error = [
              T3CCallEndReasonEnum.ERROR,
              T3CCallEndReasonEnum.UNKNOWNERROR,
              T3CCallEndReasonEnum.TOOMANYREQUEST,
              T3CCallEndReasonEnum.RESTRICTEDAREA,
              T3CCallEndReasonEnum.RESTRICTEDTIME,
              T3CCallEndReasonEnum.UNKNOWN,
              T3CCallEndReasonEnum.CLIENTERROR,
            ]
            const hangUp = [
              T3CCallEndReasonEnum.IVRGIVEUP,
              T3CCallEndReasonEnum.WAITINGGIVEUP,
              T3CCallEndReasonEnum.RINGINGGIVEUP,
            ]
            const newVal = [...(val ?? [])]
            const errIndex = newVal.findIndex(i => i === 'error')
            if (errIndex !== -1) {
              newVal.splice(errIndex, 1, ...error)
            }
            const hangIndex = newVal.findIndex(i => i === 'hangUp')
            if (hangIndex !== -1) {
              newVal.splice(hangIndex, 1, ...hangUp)
            }
            return ({ failReasonList: newVal })
          }
          // 当值为空时，不传该字段
          return ({ failReasonList: undefined })
        },
      },
    { fieldName: 'callNumber', label: '主叫电话', type: 'input' },
    { fieldName: 'satisfactionResult', label: '满意度', type: 'mapsMulti' },
    {
      fieldName: 'createTime',
      label: '记录生成时间',
      type: 'rangePicker',
      handleValue: val => ({
        createTimeFrom: formatTime(val?.[0], 'start'),
        createTimeTo: formatTime(val?.[1], 'end'),
      }),
    },
    { fieldName: 'cid', label: 'cid', type: 'multiTags' },
    {
      fieldName: 'duration',
      label: '通话时长(秒)',
      type: 'inputNumberRange',
      handleValue: val => ({
        durationFrom: val?.[0],
        durationTo: val?.[1],
      }),
    },

  ]

  const callRecordList = useMemo<RobotCallRecordData[]>(() => handleT3CCallRecord(list), [list])

  const columns: TableColumnProps<RobotCallRecordData>[] = useMemo(() => [
    { title: '智能体名称', dataIndex: 'agentName', fixed: true, width: 150, ellipsis: true },
    {
      title: '客户名称',
      dataIndex: 'customerName',
      fixed: true,
      width: 280,
      render: (name, record) => (
        <Link target="_blank" to={`/customer/${record.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
          {trimStr(name, '-')}
        </Link>),
    },
    { title: '名单标题', dataIndex: 'rollTitle', width: 180, ellipsis: true },
        {
      title: '标签',
      dataIndex: 'tags',
      width: 200,
      ellipsis: true,
      render: (tags) => (<Popover placement="topLeft" content={tags?.join('，')}> {tags?.join('，')} </Popover>),
    },
    {
      title: '记录生成时间',
      dataIndex: 'createTime',
      width: 180,
    },
    {
      title: 'cid',
      dataIndex: 'cid',
      width: 180,
      ellipsis: true,
      render: (cid) => (<Popover placement="topLeft" content={cid}> {cid} </Popover>),
    },
    {
      title: 'callId',
      dataIndex: 'qidianCallId',
      width: 180,
      ellipsis: true,
      render: (qidianCallId) => (<Popover placement="topLeft" content={qidianCallId}> {qidianCallId} </Popover>),
    },
    { title: '呼叫次数', dataIndex: 'callCount', width: 100 },
    { title: '主叫号码', dataIndex: 'callNumber', width: 180 },
    { title: '被叫号码', dataIndex: 'phone', width: 180 },
    { title: '通话结果', dataIndex: 'failReasonText', width: 180 },
    {
      title: '通话开始时间',
      dataIndex: 'callBeginTime',
      width: 180,
    },
    {
      title: '通话结束时间',
      dataIndex: 'callEndTime',
      width: 180,
    },
    { title: '通话时长', dataIndex: 'durationText', width: 120 },
    { title: '满意度', dataIndex: 'satisfactionResultText', width: 120 },
    {
      title: '操作',
      dataIndex: 'actions',
      width: 120,
      render: (_formItem, item) => {
        return (
          <ReplayRecord
            disabled={item.disabled}
            playTips={item.playTips}
            logId={`t3c-${item?.recordId}`}
            title={`与${item?.customerName}的通话录音`}
            key={item?.recordId}
            robotFlag
          />
        )
      },
    },
  ], [])

  const tableXWidth = useMemo(() => getTableXwidth(columns), [columns])

  return (
    <>
      <Card className="pt20 mb20" bordered={false}>
        <CommonFilters
          cRef={filterRef}
          className="custom-Label"
          itemList={robotCallRecordFilterItems}
          onSubmit={(filter: any) => setFilter({ ...filter, page: 1 })}
          onReset={() => resetFilter(initFilter)}
        />
      </Card>
      <Card>
        <TableDownload className="ml20" exportMethod={exportT3CRobotCallRecordList} filter={filter} />
        <Table
          loading={loading}
          scroll={{ x: tableXWidth }}
          tableLayout="fixed"
          className="mt20"
          rowKey={rowKey}
          dataSource={callRecordList}
          columns={columns}
          pagination={pagination}
        />
      </Card>
    </>
  )
}
